import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Toast } from 'primereact/toast';
import { InputTextarea } from 'primereact/inputtextarea';
import { Button } from 'primereact/button';
import { Avatar } from 'primereact/avatar';
import { Badge } from 'primereact/badge';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Divider } from 'primereact/divider';
import { ScrollPanel } from 'primereact/scrollpanel';

import './ChatBox.css';

const ChatBox = () => {
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [replyToMessageId, setReplyToMessageId] = useState(null); // Simple string state for reply ID
  const [replyToMessageText, setReplyToMessageText] = useState(''); // Store the message text for display
  const toast = useRef(null);
  const messagesEndRef = useRef(null);
  const scrollPanelRef = useRef(null);

  const API_BASE_URL = process.env.REACT_APP_API_PATH || 'https://localhost:7000/api';

  // Fetch contacts on component mount
  useEffect(() => {
    fetchContacts();
    const interval = setInterval(() => {
      fetchContacts();
      if (selectedContact) {
        fetchMessages(selectedContact.id);
      }
    }, 10000);
    return () => clearInterval(interval);
  }, [selectedContact]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchContacts = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}whatsapp/contacts`);
      console.log('Contacts fetched:', response.data);
      setContacts(response.data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to fetch contacts',
        life: 30000
      });
    }
  };

 const fetchMessages = async (contactId) => {
  if (!contactId) return;
  
  setLoadingMessages(true);
  try {
    const response = await axios.get(`${API_BASE_URL}whatsapp/messages`, {
      params: { contactId }
    });
    
    // Explicitly prioritize messageId (wamid)
    const mappedMessages = response.data.map(msg => ({
      id: msg.id, // Internal GUID
      messageId: msg.messageId || msg.messageid, // The wamid from DB
      from: msg.from || msg.from_ph,
      to: msg.to || msg.to_ph,
      body: msg.body,
      timestamp: msg.timestamp,
      type: msg.type,
      isRead: msg.isRead || msg.isread
    }));
    
    setMessages(mappedMessages);
  } catch (error) {
    console.error('Error fetching messages:', error);
  } finally {
    setLoadingMessages(false);
  }
};


  const handleContactSelect = (contact) => {
    console.log('Selected contact:', contact);
    setSelectedContact(contact);
    setReplyToMessageId(null); // Clear reply when switching contacts
    setReplyToMessageText('');
    if (contact && contact.id) {
      fetchMessages(contact.id);
    }
  };

  // Handle reply click - simplified
  const handleReplyClick = (message) => {
    const messageId = message.messageId;
    const wamid = message.messageId;
    if (!wamid || !wamid.startsWith('wamid.')) {
    console.error('❌ Cannot reply: Message lacks a valid wamid', message);
    toast.current?.show({
      severity: 'warn',
      summary: 'Cannot Reply',
      detail: 'This message cannot be replied to (missing valid WhatsApp ID).',
      life: 3000
    });
    return;
  }
    console.log('🔄 Replying to message ID:', messageId);
    console.log('📝 Message text:', message.body);
    
    setReplyToMessageId(messageId);
    setReplyToMessageText(message.body || '');
    
    // Focus on input
    setTimeout(() => {
      const inputElement = document.querySelector('.message-input');
      if (inputElement) {
        inputElement.focus();
      }
    }, 100);
  };

  // Clear reply
  const clearReply = () => {
    console.log('❌ Clearing reply');
    setReplyToMessageId(null);
    setReplyToMessageText('');
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedContact) return;

    console.log('📤 Sending message...');
    console.log('📌 Reply to message ID:', replyToMessageId);

    setSending(true);
    try {
      // Build the payload
      const payload = {
        to: selectedContact.phoneNumber || selectedContact.phonenumber,
        text: newMessage.trim(),
        replyMessageId : replyToMessageId
      };

      // Add replyMessageId if replying to a message
      if (replyToMessageId) {
        payload.replyMessageId = replyToMessageId;
        console.log('✅ Added replyMessageId to payload:', replyToMessageId);
      } else {
        console.log('ℹ️ No reply message set, sending regular message');
      }

      console.log('🚀 Final payload:', JSON.stringify(payload, null, 2));

      const response = await axios.post(`${API_BASE_URL}whatsapp/send`, payload);

      console.log('✅ Send message response:', response.data);

      if (response.status === 200) {
        setNewMessage('');
        setReplyToMessageId(null);
        setReplyToMessageText('');
        await fetchMessages(selectedContact.id);
        await fetchContacts();
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Message sent successfully',
          life: 2000
        });
      }
    } catch (error) {
      console.error('❌ Error sending message:', error);
      
      // Show more detailed error
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          'Failed to send message';
      
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: errorMessage,
        life: 3000
      });
    } finally {
      setSending(false);
    }
  };

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
      return;
    }

    if (scrollPanelRef.current) {
      try {
        const panel = scrollPanelRef.current;
        if (panel.scrollTop !== undefined) {
          panel.scrollTop = panel.scrollHeight;
        } else if (panel.getContainer) {
          const container = panel.getContainer();
          if (container) {
            container.scrollTop = container.scrollHeight;
          }
        }
      } catch (error) {
        console.warn('Error scrolling:', error);
        const container = document.querySelector('.p-scrollpanel .p-scrollpanel-content');
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      }
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    try {
      const date = new Date(timestamp);
      return date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    } catch {
      return '';
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    try {
      const date = new Date(timestamp);
      const today = new Date();
      if (date.toDateString() === today.toDateString()) {
        return 'Today';
      }
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      if (date.toDateString() === yesterday.toDateString()) {
        return 'Yesterday';
      }
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return '';
    }
  };

  const getInitials = (name) => {
    if (!name) return '??';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const hasContactData = (contact) => {
    return contact && contact.id && (contact.phoneNumber || contact.phonenumber);
  };

  return (
    <div className="chat-container">
      <Toast ref={toast} />
      
      <div className="chat-wrapper">
        {/* Contacts Sidebar */}
        <div className="contacts-sidebar">
          <div className="contacts-header">
            <h2>WhatsApp Chats</h2>
            <div className="contacts-count">
              <Badge value={contacts.length} severity="info" />
            </div>
          </div>
          
          <Divider />
          
          <ScrollPanel style={{ height: 'calc(100% - 80px)' }}>
            {contacts.length === 0 ? (
              <div className="no-contacts">
                <i className="pi pi-whatsapp" style={{ fontSize: '3rem', color: '#25D366' }}></i>
                <p>No contacts yet</p>
              </div>
            ) : (
              contacts.map((contact) => (
                <div
                  key={contact.id || contact.phoneNumber || contact.phonenumber}
                  className={`contact-item ${selectedContact?.id === contact.id ? 'active' : ''}`}
                  onClick={() => handleContactSelect(contact)}
                >
                  <Avatar 
                    label={getInitials(contact.name)} 
                    shape="circle" 
                    size="large"
                    style={{ backgroundColor: '#25D366', color: '#ffffff' }}
                  />
                  <div className="contact-info">
                    <div className="contact-name">{contact.name || contact.phoneNumber || contact.phonenumber}</div>
                    <div className="contact-last-message">
                      {contact.lastMessage || contact.lastmessage || 'No messages yet'}
                    </div>
                  </div>
                  <div className="contact-time">
                    {(contact.lastMessageTime || contact.lastmessagetime) && 
                      formatTime(contact.lastMessageTime || contact.lastmessagetime)}
                  </div>
                </div>
              ))
            )}
          </ScrollPanel>
        </div>

        {/* Chat Area */}
        <div className="chat-area">
          {!selectedContact || !hasContactData(selectedContact) ? (
            <div className="no-chat-selected">
              <i className="pi pi-comments" style={{ fontSize: '4rem', color: '#ccc' }}></i>
              <h3>Select a contact to start chatting</h3>
              <p>Choose a contact from the sidebar</p>
            </div>
          ) : (
            <>
              {/* Chat Header */}
              <div className="chat-header">
                <div className="chat-header-info">
                  <Avatar 
                    label={getInitials(selectedContact.name)} 
                    shape="circle" 
                    size="large"
                    style={{ backgroundColor: '#25D366', color: '#ffffff' }}
                  />
                  <div className="chat-header-details">
                    <div className="chat-header-name">
                      {selectedContact.name || selectedContact.phoneNumber || selectedContact.phonenumber}
                    </div>
                    <div className="chat-header-phone">
                      {selectedContact.phoneNumber || selectedContact.phonenumber}
                    </div>
                  </div>
                </div>
              </div>

              {/* Messages Area */}
              <div className="messages-area">
                <ScrollPanel ref={scrollPanelRef} style={{ height: '100%' }}>
                  {loadingMessages ? (
                    <div className="loading-messages">
                      <ProgressSpinner style={{ width: '50px', height: '50px' }} />
                      <p>Loading messages...</p>
                    </div>
                  ) : messages.length === 0 ? (
                    <div className="no-messages">
                      <i className="pi pi-inbox" style={{ fontSize: '3rem', color: '#ccc' }}></i>
                      <p>No messages yet</p>
                      <p className="no-messages-sub">Send a message to start the conversation</p>
                    </div>
                  ) : (
                    <>
                      {messages.map((message, index) => {
                        const showDate = index === 0 || 
                          (message.timestamp && messages[index - 1]?.timestamp && 
                          new Date(message.timestamp).toDateString() !== 
                          new Date(messages[index - 1].timestamp).toDateString());
                        
                        const isFromBusiness = message.from === 'BUSINESS';
                        const isOutgoing = isFromBusiness || message.type === 'OUT' || message.type === 'sent';
                        const messageId = message.messageId || message.id;
                        const isReplyingTo = replyToMessageId === messageId;
                        
                        return (
                          <React.Fragment key={message.id || index}>
                            {showDate && (
                              <div className="message-date-divider">
                                <span>{formatDate(message.timestamp)}</span>
                              </div>
                            )}
                            <div className={`message ${isOutgoing ? 'message-out' : 'message-in'} ${isReplyingTo ? 'message-highlight' : ''}`}>
                              <div className="message-bubble">
                                <div className="message-text">{message.body || 'No content'}</div>
                                <div className="message-time">
                                  {formatTime(message.timestamp)}
                                  {isOutgoing && (
                                    <i className={`pi ${message.isRead ? 'pi-check-circle' : 'pi-check'} message-status`}></i>
                                  )}
                                  {/* Reply button - only for incoming messages */}
                                  {!isOutgoing && (
                                    <Button
                                      icon="pi pi-reply"
                                      className="p-button-text p-button-sm reply-button"
                                      onClick={() => handleReplyClick(message)}
                                      tooltip="Reply to this message"
                                      tooltipOptions={{ position: 'top' }}
                                    />
                                  )}
                                </div>
                              </div>
                            </div>
                          </React.Fragment>
                        );
                      })}
                      <div ref={messagesEndRef} />
                    </>
                  )}
                </ScrollPanel>
              </div>

              {/* Reply Indicator */}
              {replyToMessageId && (
                <div className="reply-indicator">
                  <div className="reply-content">
                    <i className="pi pi-reply" style={{ transform: 'scaleX(-1)' }}></i>
                    <span>
                      Replying to: {replyToMessageText.substring(0, 50)}
                      {replyToMessageText.length > 50 && '...'}
                    </span>
                    <span className="reply-id-badge">
                      ID: {replyToMessageId}
                    </span>
                  </div>
                  <Button
                    icon="pi pi-times"
                    className="p-button-text p-button-sm"
                    onClick={clearReply}
                  />
                </div>
              )}

              {/* Message Input */}
              <div className="message-input-area">
                <div className="message-input-wrapper">
                  <InputTextarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder={replyToMessageId ? "Type your reply..." : "Type a message..."}
                    rows={1}
                    className="message-input"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        sendMessage();
                      }
                    }}
                    disabled={sending}
                  />
                  <Button
                    icon="pi pi-send"
                    className="send-button"
                    onClick={sendMessage}
                    loading={sending}
                    disabled={!newMessage.trim() || sending}
                    rounded
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatBox;